import { apiClient } from '@/api/client';

/**
 * One repeatable geography-preference row (US-040/US-101 AC6/AC4).
 * `countryId`/`stateId` reference the shared geography reference tables
 * (MYN-46, `@/api/geography.api`) - the same source the startup HQ
 * dropdowns use.
 */
export interface InvestorGeography {
  worldRegion: string;
  countryId: number;
  stateId?: number | null;
}

/**
 * One colleague contact (MYN-202, FR-175): a named person to contact for
 * opportunities in one sector. Contact-only - no account, invite or email.
 * `sectorId` is a platform `Sector` id (`GET /taxonomy/sectors`), NOT an
 * investor industry-preference value; at most one row per sector.
 */
export interface InvestorColleagueContact {
  /** READ-ONLY. The Backend rejects it on PATCH (`forbidNonWhitelisted`). */
  id?: string;
  sectorId: number;
  name: string;
  email: string;
}

/**
 * Investor entity + profile, as returned by `GET/PATCH /investors/:entityId`
 * (mirrors the backend `InvestorProfile` + relations). Every field is
 * optional/nullable. GET always returns a body (MYN-278): a profile that does
 * not exist yet comes back as a shell, `{ id: null, entityId, entityName,
 * matchingReadinessPct: 0, matchingMissingFields: [every readiness key] }`,
 * and the first Save creates it. Decimal fields (`prefAmountLow/High`) come
 * over the wire as strings.
 */
export interface InvestorProfileData {
  /** `null` on the shell of a profile that does not exist yet; set once it does. */
  id?: string | null;
  entityId?: string;
  /**
   * READ-ONLY. The investment firm's name: the entity name the person was invited
   * under (FR-187). Never part of a save payload.
   */
  entityName?: string | null;
  /**
   * READ-ONLY. The STORED matching-readiness percentage (0-100), recomputed by the
   * backend on every save. Absent on an older response, which readers treat as 0.
   * The notice and the dashboard card read it; nothing recomputes it here.
   */
  matchingReadinessPct?: number;
  /**
   * READ-ONLY. Readiness keys still missing (`INVESTOR_READINESS_KEYS`), computed
   * by the backend, never in the frontend (profile-redesign FR-170).
   */
  matchingMissingFields?: string[];
  // Identity
  firmName?: string | null;
  investorType?: string | null;
  contactName?: string | null;
  contactTitle?: string | null;
  email?: string | null;
  phone?: string | null;
  websiteUrl?: string | null;
  linkedinUrl?: string | null;
  /**
   * READ-ONLY. Presigned URLs the backend resolved from the stored S3 keys
   * (MYN-175). Safe as an image source, and never valid to send back -
   * `UpdateInvestorProfileDto` declares no such fields, and the API runs
   * `forbidNonWhitelisted`, so including either rejects the ENTIRE request.
   */
  logoUrl?: string | null;
  profilePhotoUrl?: string | null;
  /**
   * WRITE-ONLY. The raw S3 keys the upload flow returns. Omit them to leave
   * the stored image alone; `''` is the backend's explicit "clear it" signal.
   *
   * This interface doubles as the read AND write shape, which is what let the
   * two halves drift apart unnoticed. Splitting it is worthwhile but outside
   * this change.
   */
  logoFileKey?: string;
  profilePhotoFileKey?: string;
  intro?: string | null;
  // Location
  addressStreet?: string | null;
  addressStreet2?: string | null;
  addressCity?: string | null;
  addressState?: string | null;
  addressPostalCode?: string | null;
  addressCountry?: string | null;
  // Investment preferences (Capital Raise matching, US-101)
  prefAmountLow?: string | number | null;
  prefAmountHigh?: string | number | null;
  prefMinRevenue?: string | null;
  prefRecurringRevenue?: string | null;
  prefFemaleLed?: string | null;
  prefUnderrepresented?: string | null;
  prefB2b?: string | null;
  prefB2c?: string | null;
  prefB2g?: string | null;
  prefSaas?: string | null;
  prefAi?: string | null;
  prefLeadOrFollow?: string | null;
  activelyDeploying?: boolean;
  prefOtherCriteria?: string | null;
  prefUnderrepresentedCategories?: string[];
  interestOfficeHours?: boolean;
  interestFounderFunderEvents?: boolean;
  isPrivate?: boolean;
  industries?: string[];
  geographies?: InvestorGeography[];
  preferredStages?: string[];
  /**
   * Sent on PATCH only as `{ sectorId, name, email }` rows. Absent leaves the
   * stored rows untouched; `[]` clears them (MYN-202).
   */
  colleagueContacts?: InvestorColleagueContact[];
}

export async function fetchInvestorProfile(entityId: string): Promise<InvestorProfileData> {
  // Always a body since MYN-278 (a shell for a profile that does not exist yet). A null
  // body (a deploy that has not caught up, any empty 200) must not crash the page: it
  // reads as the same shell, so the profile shows as not set up yet.
  const { data } = await apiClient.get<InvestorProfileData | null>(`/investors/${entityId}`);
  return data ?? { id: null, entityId };
}

export async function saveInvestorProfile(
  entityId: string,
  payload: InvestorProfileData,
): Promise<InvestorProfileData> {
  const { data } = await apiClient.patch<InvestorProfileData>(`/investors/${entityId}`, payload);
  return data;
}

export async function deleteInvestorProfile(entityId: string): Promise<void> {
  await apiClient.delete(`/investors/${entityId}`);
}
